(() => {
  // Image URLs carry width=400 only; the 200w and 600w candidates are derived here to keep the header HTML small.
  const srcsetFor = (src) => [200, 400, 600].map((width) => `${src.replace(/([?&])width=\d+/, `$1width=${width}`)} ${width}w`).join(', ');

  const loadImages = (root) => {
    root.querySelectorAll('img[data-src]').forEach((img) => {
      img.srcset = srcsetFor(img.dataset.src);
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
  };

  // Touch has no hover preview, so below 1000px each device name gets its own thumbnail, built only when its list opens.
  const loadThumbs = (root) => {
    if (root.dataset.thumbs) return;
    root.dataset.thumbs = 'true';
    root.querySelectorAll('.rbx-mega__device[data-src] .rbx-mega__thumb').forEach((thumb) => {
      const img = document.createElement('img');
      img.className = 'rbx-mega__thumb-img';
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.decoding = 'async';
      img.sizes = '56px';
      img.srcset = srcsetFor(thumb.parentElement.dataset.src);
      img.src = thumb.parentElement.dataset.src;
      thumb.append(img);
    });
  };

  document.getElementById('mobile-menu')?.addEventListener('click', (event) => {
    const button = event.target.closest('[data-action="open-panel"]');
    const level = button && document.getElementById(button.getAttribute('aria-controls'));
    const list = level?.querySelector('.rbx-mega-drawer');
    if (!list) return;
    // The drawer shell is filled from the desktop panel so the page carries one copy of each menu's lists.
    if (list.dataset.rbxMegaSource && !list.childElementCount) {
      const source = document.getElementById(list.dataset.rbxMegaSource)?.querySelector('.rbx-mega__main');
      if (source) {
        list.innerHTML = source.innerHTML;
        list.querySelectorAll('.rbx-mega__thumb-img').forEach((img) => img.remove());
      }
    }
    loadImages(list);
    loadThumbs(list);
  }, true);

  const nav = document.querySelector('[data-rbx-mega-nav]');
  if (!nav) return;

  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 220;
  const desktop = window.matchMedia('(min-width: 1000px)');
  const clickMode = nav.dataset.openTrigger === 'click';
  const triggers = [...nav.querySelectorAll('[data-rbx-mega-trigger]')];
  const navLinks = [...nav.querySelectorAll('.nav-bar__link')];
  const backdrop = nav.querySelector('[data-rbx-mega-backdrop]');

  let current = null;
  let openTimer = null;
  let closeTimer = null;
  let lastPointerType = 'mouse';
  let refocusing = false;
  let sheet = false;

  const panelFor = (trigger) => document.getElementById(trigger.getAttribute('aria-controls'));
  const itemFor = (trigger) => trigger.closest('.nav-bar__item');
  const focusables = (panel) => [...panel.querySelectorAll('a[href], button:not([disabled])')];

  const clearTimers = () => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
  };

  const hydrate = (panel) => {
    loadImages(panel);
    if (!desktop.matches) {
      loadThumbs(panel);
      return;
    }
    const preview = panel.querySelector('[data-rbx-preview]');
    if (!preview || preview.activeDevice) return;
    const first = panel.querySelector('[data-rbx-device]:not(.rbx-mega__group-heading)') || panel.querySelector('[data-rbx-device]');
    if (first) setPreview(panel, first);
  };

  // Below 1000px the panel is a modal sheet under the nav bar; the nav bar stays tappable so the same item closes it.
  const openSheet = (panel) => {
    sheet = true;
    document.documentElement.style.setProperty('--rbx-mega-top', `${Math.max(0, nav.getBoundingClientRect().bottom)}px`);
    document.documentElement.classList.add('rbx-mega-locked');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', panel.querySelector('.rbx-mega__title').textContent.trim());
    panel.scrollTop = 0;
    backdrop?.classList.add('is-visible');
    panel.querySelector('[data-rbx-mega-close]')?.focus({ preventScroll: true });
  };

  const closeSheet = (panel, trigger) => {
    sheet = false;
    document.documentElement.classList.remove('rbx-mega-locked');
    backdrop?.classList.remove('is-visible');
    const hadFocus = panel.contains(document.activeElement);
    panel.removeAttribute('role');
    panel.removeAttribute('aria-modal');
    panel.removeAttribute('aria-label');
    if (hadFocus) trigger.focus({ preventScroll: true });
  };

  const setPreview = (panel, device) => {
    const preview = panel.querySelector('[data-rbx-preview]');
    if (!preview || preview.activeDevice === device) return;

    panel.querySelector('.rbx-mega__device.is-previewed')?.classList.remove('is-previewed');
    device.classList.add('is-previewed');
    preview.activeDevice = device;
    preview.querySelector('.rbx-mega__preview-name').textContent = device.dataset.name;

    const placeholder = preview.querySelector('.rbx-mega__preview-placeholder');
    const [imgA, imgB] = preview.querySelectorAll('.rbx-mega__preview-img');
    const shown = preview.querySelector('.rbx-mega__preview-img.is-visible');

    if (!device.dataset.src) {
      preview.classList.remove('is-loading');
      shown?.classList.remove('is-visible');
      placeholder.classList.add('is-visible');
      return;
    }

    const next = shown === imgA ? imgB : imgA;
    const reveal = () => {
      if (preview.activeDevice !== device) return;
      preview.classList.remove('is-loading');
      next.classList.add('is-visible');
      if (shown && shown !== next) shown.classList.remove('is-visible');
      placeholder.classList.remove('is-visible');
    };
    preview.classList.add('is-loading');
    next.onload = reveal;
    next.srcset = srcsetFor(device.dataset.src);
    next.src = device.dataset.src;
    if (next.complete && next.naturalWidth) reveal();
  };

  const open = (trigger) => {
    clearTimers();
    if (current === trigger) return;
    if (current) close();
    const panel = panelFor(trigger);
    if (!panel) return;
    hydrate(panel);
    panel.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    current = trigger;
    if (!desktop.matches) openSheet(panel);
  };

  const close = () => {
    clearTimers();
    if (!current) return;
    const trigger = current;
    const panel = panelFor(trigger);
    panel?.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    current = null;
    if (sheet && panel) closeSheet(panel, trigger);
  };

  const scheduleClose = () => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(close, CLOSE_DELAY);
  };

  const isInside = (node) => current && node instanceof Node && (itemFor(current).contains(node) || panelFor(current).contains(node));

  triggers.forEach((trigger) => {
    const item = itemFor(trigger);
    const panel = panelFor(trigger);
    if (!panel) return;

    item.addEventListener('mouseenter', () => {
      if (clickMode || !desktop.matches || lastPointerType === 'touch') return;
      clearTimers();
      openTimer = setTimeout(() => open(trigger), OPEN_DELAY);
    });
    item.addEventListener('mouseleave', () => {
      if (!desktop.matches) return;
      if (!clickMode && current === trigger) scheduleClose();
      else clearTimeout(openTimer);
    });
    panel.addEventListener('mouseenter', () => {
      if (current === trigger) clearTimers();
    });
    panel.addEventListener('mouseleave', () => {
      if (!clickMode && desktop.matches && current === trigger) scheduleClose();
    });

    panel.querySelector('[data-rbx-mega-close]')?.addEventListener('click', close);

    trigger.addEventListener('click', (event) => {
      if (clickMode || !desktop.matches) {
        event.preventDefault();
        current === trigger ? close() : open(trigger);
      } else if (lastPointerType === 'touch' && current !== trigger) {
        event.preventDefault();
        open(trigger);
      }
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && !event.shiftKey && current === trigger) {
        const first = focusables(panel)[0];
        if (first) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const items = focusables(panel);
      if (sheet) {
        const edge = event.shiftKey ? items[0] : items[items.length - 1];
        if (document.activeElement === edge) {
          event.preventDefault();
          (event.shiftKey ? items[items.length - 1] : items[0]).focus();
        }
      } else if (event.shiftKey && document.activeElement === items[0]) {
        event.preventDefault();
        trigger.focus();
      } else if (!event.shiftKey && document.activeElement === items[items.length - 1]) {
        const nextLink = navLinks[navLinks.indexOf(trigger) + 1];
        if (nextLink) {
          event.preventDefault();
          nextLink.focus();
        }
      }
    });

    const preview = (event) => {
      if (!desktop.matches) return;
      const device = event.target.closest('[data-rbx-device]');
      if (device) setPreview(panel, device);
    };
    panel.addEventListener('mouseover', preview);
    panel.addEventListener('focusin', preview);
  });

  // Keyboard focus opens the panel; focus moving anywhere outside the open trigger and panel closes it.
  document.addEventListener('focusin', (event) => {
    if (!desktop.matches) return;
    const trigger = event.target.closest?.('[data-rbx-mega-trigger]');
    if (trigger && lastPointerType !== 'touch' && !refocusing) {
      open(trigger);
    } else if (current && !isInside(event.target)) {
      close();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !current) return;
    const trigger = current;
    const hadFocus = isInside(document.activeElement);
    close();
    if (hadFocus) {
      refocusing = true;
      trigger.focus({ preventScroll: true });
      refocusing = false;
    }
  });

  document.addEventListener('pointerdown', (event) => {
    lastPointerType = event.pointerType || 'mouse';
    if (current && !isInside(event.target)) close();
  }, true);

  // Mouse movement means the last pointer is no longer touch, so hover-open resumes on hybrid devices.
  document.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'mouse') lastPointerType = 'mouse';
  }, { passive: true });

  // The backdrop is not focusable, so a tap on it would otherwise blur the trigger that close() just refocused.
  backdrop?.addEventListener('mousedown', (event) => event.preventDefault());

  desktop.addEventListener('change', close);
})();
